import React, { useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { makeStyles } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InstructionComponentType, InstructionDiffStatus } from '@scale/shared/defaults';
import { useProjectInstructionContext } from '../projectInstructionContext';
import { DIFF_STATUS_LABEL, NormalizedComponentProps } from '../types';
import Timer from './timer';

interface ISideNavigationItem {
  component: NormalizedComponentProps;
  nested: boolean;
  isGroupOpen: boolean;
  disableTimer?: boolean;
  // onClickItem: () => void;
  onClickItem: (componentOverride?: any) => void;
  getComponentById?: (compId: string) => any | undefined;
}

const getDiffStatus = (component: NormalizedComponentProps): InstructionDiffStatus | false => {
  if (component.components) {
    const innerDiffStatus = component.components.find(getDiffStatus);
    if (innerDiffStatus) {
      return InstructionDiffStatus.Addition;
    }
    return false;
  }
  if (component.content && 'diffStatus' in component.content && component.content.diffStatus) {
    return component.content.diffStatus;
  }
  return false;
};

// eslint-disable-next-line complexity
const SideNavigationItem = ({
  component,
  nested,
  isGroupOpen,
  disableTimer,
  onClickItem,
  getComponentById,
}: ISideNavigationItem) => {
  const classes = useStyles();
  const [subAttributesOpen, setSubAttributesOpen] = useState<boolean>(false);
  const [subLabelsOpen, setSubLabelsOpen] = useState<boolean>(false);
  const { selectedPath, setSelectedPath, isOnboarding } = useProjectInstructionContext();
  let { nodeId, nodeName, nodeType, iconName, iconColor, waitSeconds } = component;
  const [selectedType, selectedId] = selectedPath;

  const isSelected = selectedType === nodeType && selectedId === nodeId;
  const isGroupComponent = nodeType === InstructionComponentType.Group;

  if (isGroupComponent && component.components?.length) {
    waitSeconds = component.components.reduce((prev, comp) => prev + Number(comp.waitSeconds), 0);
  }

  const DiffLabel = ({ diffStatus }: { diffStatus: InstructionDiffStatus }) => {
    return (
      <span
        className={cx(
          classes.diffLabel,
          diffStatus === InstructionDiffStatus.Deletion && classes.diffRemoved,
        )}
      >
        {DIFF_STATUS_LABEL[diffStatus]}
      </span>
    );
  };
  const diffStatus = getDiffStatus(component);
  return (
    <>
      <span
        className={cx(classes.tab, {
          [classes.nested]: nested,
          [classes.deletedComponent]: diffStatus === InstructionDiffStatus.Deletion,
          [classes.highlightedComponent]:
            !isSelected &&
            waitSeconds &&
            !isOnboarding &&
            (diffStatus === InstructionDiffStatus.Addition ||
              diffStatus === InstructionDiffStatus.Update),
          [classes.selectedComponent]: isSelected,
        })}
        onClick={() => {
          onClickItem(component);
        }}
      >
        <span className={cx(classes.componentHeader)}>
          <span className={classes.typeIcon} style={{ background: iconColor + '26' }}>
            <FontAwesomeIcon icon={iconName} color={iconColor} />
          </span>

          {_.startCase(nodeName)}
          {isGroupComponent && (
            <div style={{ marginLeft: '10px' }}>
              <FontAwesomeIcon
                icon={isGroupOpen ? 'chevron-down' : 'chevron-right'}
                color="var(--color-RemoGray50)"
              />
            </div>
          )}
          {diffStatus && <DiffLabel diffStatus={diffStatus} />}
          {!isGroupComponent && waitSeconds && isSelected && !disableTimer && (
            <Timer waitSeconds={waitSeconds} />
          )}
          {component.content?.hiddenFromCustomer && (
            <FontAwesomeIcon
              icon="eye-slash"
              color="var(--color-RemoGray50)"
              style={{ marginLeft: '10px' }}
            />
          )}
        </span>
      </span>
      {isGroupComponent && component.components && isGroupOpen && (
        <div style={{ marginLeft: '14px' }}>
          {component.components.map(groupSectionComponent => (
            <SideNavigationItem
              key={groupSectionComponent.nodeId}
              nested={true}
              component={groupSectionComponent}
              isGroupOpen={false}
              onClickItem={() => {
                setSelectedPath([groupSectionComponent.nodeType, groupSectionComponent.nodeId]);
              }}
            />
          ))}
        </div>
      )}
      {component.content?.nestedAttributeIds &&
        component.content.nestedAttributeIds.length > 0 &&
        getComponentById && (
          <div className={classes.subComponent}>
            <div className={classes.subComponentHeader}>
              <FontAwesomeIcon
                icon={subAttributesOpen ? 'chevron-down' : 'chevron-right'}
                color="var(--color-RemoGray50)"
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSubAttributesOpen(!subAttributesOpen);
                }}
              />
              <div
                className={classes.subComponentText}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSubAttributesOpen(!subAttributesOpen);
                }}
              >
                SubAttributes
              </div>
            </div>
            {subAttributesOpen &&
              component.content?.nestedAttributeIds.map(nestedAttributeId => {
                const component: any | undefined = getComponentById(nestedAttributeId);
                if (!component) {
                  return <></>;
                }
                return (
                  <a
                    key={component.content._id}
                    className={classes.subComponentTitle}
                    href={`#${component.content._id}`}
                  >
                    <SideNavigationItem
                      key={component.content._id}
                      nested={true}
                      component={component}
                      isGroupOpen={isGroupOpen}
                      onClickItem={onClickItem}
                      getComponentById={getComponentById}
                    />
                  </a>
                );
              })}
          </div>
        )}

      {component.content?.nestedLabelIds &&
        component.content.nestedLabelIds.length > 0 &&
        getComponentById && (
          <div className={classes.subComponent}>
            <div className={classes.subComponentHeader}>
              <FontAwesomeIcon
                icon={subLabelsOpen ? 'chevron-down' : 'chevron-right'}
                color="var(--color-RemoGray50)"
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSubLabelsOpen(!subLabelsOpen);
                }}
              />
              <div
                className={classes.subComponentText}
                onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSubLabelsOpen(!subLabelsOpen);
                }}
              >
                SubLabels
              </div>
            </div>
            {subLabelsOpen &&
              component.content?.nestedLabelIds.map(nestedLabelId => {
                const component: any | undefined = getComponentById(nestedLabelId);
                if (!component) {
                  return <></>;
                }
                // Todo: revisit this dumb zoom
                // the subcomponents use hrefs to get zoomed to, the parent components use a timer
                return (
                  <a
                    key={component.content._id}
                    // style={{
                    //   textDecoration: 'none',
                    //   textDecorationColor: 'none',
                    // }}
                    className={classes.subComponentTitle}
                    href={`#${component.content._id}`}
                  >
                    <SideNavigationItem
                      key={component.content._id}
                      nested={true}
                      component={component}
                      isGroupOpen={isGroupOpen}
                      onClickItem={onClickItem}
                      getComponentById={getComponentById}
                    />
                  </a>
                );
              })}
          </div>
        )}
    </>
  );
};

const useStyles = makeStyles(theme => ({
  scrollBehavior: {
    scrollBehavior: 'smooth',
    textDecoration: 'none',
  },
  nested: {
    paddingLeft: '20px !important',
  },
  selected: {
    background: '#F3F2FF',
    color: '#6A5DF9',
  },
  selectedComponent: {
    background: theme.colors.gray05,
    padding: 10,
  },
  componentHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  subComponent: {
    padding: '10px',
    alignItems: 'center',
    marginLeft: '14px',
  },
  subComponentHeader: {
    display: 'flex',
    flexDirection: 'row',
    cursor: 'pointer',
    alignItems: 'center',
    '&:hover': {
      color: '#6A5DF9',
    },
  },
  subComponentText: {
    marginLeft: '14px',
  },
  subComponentTitle: {
    color: '#000002',
    textDecoration: 'none',
  },
  diffLabel: {
    color: theme.colors.green,
    borderRadius: 30,
    background: '#F1F8F5',
    fontSize: 10,
    padding: '2px 5px',
    paddingLeft: '5px !important',
    margin: '0 3px',
    height: 15,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  diffRemoved: {
    color: 'red',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    padding: 10,
    '&:hover': {
      color: '#6A5DF9',
    },
  },
  typeIcon: {
    width: 15,
    height: 15,
    borderRadius: 3,
    fontSize: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
    flexShrink: 0,
  },
  highlightedComponent: {
    background: '#fffecd',
  },
  deletedComponent: {
    opacity: 0.5,
  },
}));

export default SideNavigationItem;
